import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { useAuth } from '@/contexts/AuthContext';
import { Building2, CheckCircle } from 'lucide-react';

export default function OrganizationSignup() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Company Info
    company: '',
    subdomain: '',
    industry: '',
    companySize: '',
    // Step 2: Admin User
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);

  const industries = [
    'Technology',
    'Healthcare',
    'Finance',
    'Retail',
    'Manufacturing',
    'Education',
    'Consulting',
    'Other',
  ];

  const companySizes = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-500 employees',
    '501-1000 employees',
    '1000+ employees',
  ];

  const handleSubdomainChange = (value: string) => {
    // Auto-format subdomain
    const formatted = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setFormData({ ...formData, subdomain: formatted });
    
    // TODO: Check subdomain availability with API
    if (formatted.length >= 3) {
      setSubdomainAvailable(true); // Mock - replace with actual API call
    } else {
      setSubdomainAvailable(null);
    }
  };

  const handleStep1Next = () => {
    if (!formData.company || !formData.subdomain) {
      setError('Please fill in all required fields');
      return;
    }
    if (formData.subdomain.length < 3) {
      setError('Subdomain must be at least 3 characters');
      return;
    }
    if (!subdomainAvailable) {
      setError('Please choose an available subdomain');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (formData.password.length < 12) {
      setError('Password must be at least 12 characters long');
      setIsLoading(false);
      return;
    }

    try {
      await signup({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: 'org_owner', // First user is always org owner
        accountType: 'organization',
        company: formData.company,
        subdomain: formData.subdomain,
        industry: formData.industry,
        companySize: formData.companySize,
      });
      
      // Redirect to email verification
      navigate(`/verify-email?email=${encodeURIComponent(formData.email)}&type=organization`);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Signup failed. Please try again or contact support.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center items-center space-x-2">
          <Building2 className="h-10 w-10 text-primary-600" />
          <span className="text-2xl font-bold text-gray-900">ePeople Agent</span>
        </Link>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Create your organization
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Start your 14-day free trial • No credit card required
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border border-gray-200">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-2">
              {[1, 2].map((s) => (
                <div
                  key={s}
                  className={`h-2 w-24 rounded-full ${
                    s <= step ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <p className="text-center text-sm text-gray-600 mt-2">
              Step {step} of 2
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {step === 1 && (
              <>
                <Input
                  label="Company Name"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Acme Corporation"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Workspace URL <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center space-x-2">
                    <Input
                      value={formData.subdomain}
                      onChange={(e) => handleSubdomainChange(e.target.value)}
                      placeholder="acme"
                      helperText="Only lowercase letters, numbers, and hyphens"
                    />
                    <span className="text-sm text-gray-500 whitespace-nowrap">.epeople.work</span>
                  </div>
                  {subdomainAvailable === true && formData.subdomain && (
                    <div className="flex items-center mt-1 text-sm text-green-600">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Available
                    </div>
                  )}
                  {subdomainAvailable === false && (
                    <p className="mt-1 text-sm text-red-600">Subdomain not available</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Industry
                  </label>
                  <select
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select industry</option>
                    {industries.map((industry) => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Size
                  </label>
                  <select
                    value={formData.companySize}
                    onChange={(e) => setFormData({ ...formData, companySize: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select company size</option>
                    {companySizes.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>

                <Button type="button" className="w-full" onClick={handleStep1Next}>
                  Continue
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Creating account for:</strong> {formData.company}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Workspace: <strong>{formData.subdomain}.epeople.work</strong>
                  </p>
                </div>

                <Input
                  label="First Name"
                  required
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="John"
                />

                <Input
                  label="Last Name"
                  required
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder="Doe"
                />

                <Input
                  label="Work Email"
                  type="email"
                  required
                  autoComplete="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@acme.com"
                />

                <Input
                  label="Password"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  helperText="At least 12 characters with uppercase, lowercase, number, and symbol"
                />

                <Input
                  label="Confirm Password"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                />

                <div className="flex space-x-4">
                  <Button type="button" variant="outline" className="w-full" onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <Button type="submit" className="w-full" isLoading={isLoading}>
                    Create Organization
                  </Button>
                </div>
              </>
            )}
          </form>

          <div className="mt-6">
            <p className="text-center text-xs text-gray-500">
              By signing up, you agree to our{' '}
              <Link to="/terms" className="text-primary-600 hover:text-primary-500">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-primary-600 hover:text-primary-500">
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
